import React from 'react';
import { Drawer, Box, List } from '@mui/material';
import NavigationItem from './NavigationItem';
import CollapsibleNavigationItem from './CollapsibleNavigationItem';
import TopNavigationIcon from './TopNavigationIcon';
import ImpersonationControls from './ImpersonationControls';
import UserAvatarItem from './UserAvatarItem';

/**
 * DesktopDrawer component for rendering the desktop sidebar drawer
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.miniDrawer - Whether the drawer is in mini mode
 * @param {Function} props.onDrawerOpen - Function to expand the drawer
 * @param {Function} props.onDrawerClose - Function to collapse the drawer
 * @param {Array} props.navigationItems - Array of navigation items
 * @param {Object} props.openCollapsible - Object tracking which collapsible items are open
 * @param {Function} props.onToggleCollapsible - Function to toggle collapsible items
 * @param {boolean} props.isImpersonating - Whether user impersonation is active
 * @param {Object} props.user - Current user
 * @param {Object} props.originalUser - Original user when impersonating
 * @param {boolean} props.hasAdminAccess - Whether the user has admin access
 * @param {Function} props.onStartImpersonation - Function to start impersonation
 * @param {Function} props.onStopImpersonation - Function to stop impersonation
 * @param {Function} props.onLogout - Function to handle logout
 * @returns {JSX.Element} DesktopDrawer component
 */
const DesktopDrawer = ({
  miniDrawer,
  navigationItems,
  openCollapsible,
  onToggleCollapsible,
  isImpersonating,
  user,
  originalUser,
  hasAdminAccess,
  onStartImpersonation,
  onStopImpersonation,
  onLogout
}) => {
  // Filter navigation items based on permissions
  const filteredItems = navigationItems.filter(item => {
    // Skip items that require admin if user doesn't have admin access
    if (item.requiredPermission === 'admin' && !hasAdminAccess) {
      return false;
    }
    // Skip mobile-only items
    if (item.mobileOnly) {
      return false;
    }
    return true;
  });

  // Find the top icon item
  const topIconItem = filteredItems.find(item => item.isTopIcon);
  
  // Regular navigation items (excluding top icon)
  const regularItems = filteredItems.filter(item => !item.isTopIcon);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: miniDrawer ? 'var(--sidebar-collaped-width)' : 'var(--sidebar-expanded-width)',
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: miniDrawer ? 'var(--sidebar-collaped-width)' : 'var(--sidebar-expanded-width)',
          height: 'calc(100dvh - var(--topnavbar-height))',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          borderRadius: '0',
          top: 'var(--topnavbar-height)',
          bgcolor: 'transparent',
          borderWidth: '0',
          p: 'var(--sidebar-container-padding) 0 var(--sidebar-container-padding) var(--sidebar-container-padding)',
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'var(--bg-panel)',
          backdropFilter: 'var(--effect-panel-backdrop-filter)',
          border: 'var(--border-width) solid',
          borderColor: 'var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >

        {/* Top portion: menu items */}
        <Box sx={{ p: 1.25, flexGrow: 1, overflowY: 'auto' }}>
          <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 0 }}>
            {/* Top icon - only for navigation to chat */}
            {topIconItem && (
              <TopNavigationIcon
                item={topIconItem}
                miniDrawer={miniDrawer}
                isDesktop={true}
              />
            )}

            {/* Regular navigation items */}
            {regularItems.map((item) => (
              item.isCollapsible ? (
                <CollapsibleNavigationItem
                  key={item.id}
                  item={item}
                  miniDrawer={miniDrawer}
                  isDesktop={true}
                  open={openCollapsible[item.id] || false}
                  onToggle={() => onToggleCollapsible(item.id)}
                />
              ) : (
                <NavigationItem
                  key={item.id}
                  item={item}
                  miniDrawer={miniDrawer}
                  isDesktop={true}
                />
              )
            ))}
          </List>
        </Box>

        {/* Bottom portion: impersonation controls and logout */}
        <Box sx={{ p: 1.25 }}>
          <ImpersonationControls
            isImpersonating={isImpersonating}
            hasAdminAccess={hasAdminAccess}
            miniDrawer={miniDrawer}
            isDesktop={true}
            onStartImpersonation={onStartImpersonation}
            onStopImpersonation={onStopImpersonation}
          />

          {/* User avatar with dropdown menu */}
          <UserAvatarItem
            user={user}
            miniDrawer={miniDrawer}
            isDesktop={true}
            onLogout={onLogout}
          />
        </Box>
      </Box>
    </Drawer>
  );
};

export default DesktopDrawer;
